import React, {
  ReactNode,
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { useLogoutTimer } from "hooks";
import {
  signUp,
  confirmSignUp,
  resendSignUpCode,
  signIn,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  signOut,
  fetchAuthSession,
  decodeJWT,
  getCurrentUser,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { AuthState, UserRole, UserType } from "types";
import { getDomain } from "tldts";

export interface AuthProviderProps {
  children: ReactNode;
}

export interface AuthContextType {
  authState: AuthState;
  setAuthState: React.Dispatch<React.SetStateAction<AuthState>>;
  handleSignUp: (data: {
    username: string;
    password: string;
    domain: string;
    userType: UserType;
  }) => Promise<Error | void>;
  handleSignUpConfirmation: (data: {
    username: string;
    confirmationCode: string;
  }) => Promise<Error | void>;
  resendConfirmationCode: (username: string) => Promise<Error | void>;
  handleSignIn: (data: {
    username: string;
    password: string;
  }) => Promise<Error | void>;
  handleSignInConfirmation: (totpCode: string) => Promise<Error | void>;
  handleForceNewPassword: (newPassword: string) => Promise<Error | void>;
  handleResetPassword: (username: string) => Promise<Error | void>;
  handleConfirmResetPassword: (data: {
    username: string;
    confirmationCode: string;
    newPassword: string;
  }) => Promise<Error | void>;
  handleSignOut: () => Promise<Error | void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const useAuthCheck = (
  setAuthState: React.Dispatch<React.SetStateAction<AuthState>>,
): void => {
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await getCurrentUser();
        const session = await fetchAuthSession();
        const token = session?.tokens?.idToken?.toString() ?? "";
        const payload = decodeJWT(token)?.payload;
        const userType = payload?.[`custom:userType`] as UserType;
        const role = payload?.[`custom:role`] as UserRole;
        const domain = payload?.[`custom:domain`] as string;

        setAuthState((prevState) => ({
          ...prevState,
          userType: userType,
          role: role,
          domain: domain,
          nextStep: "SIGN_IN",
          isSignedIn: true,
          user: user || undefined,
        }));
      } catch {
        setAuthState((prevState) => ({
          ...prevState,
          userType: UserType.STUDENT,
          nextStep: "SIGN_IN",
          isSignedIn: false,
          user: undefined,
        }));
      }
    };

    checkAuth();

    const authListener = (data: any) => {
      switch (data.payload.event) {
        case "signIn":
        case "signUp":
          checkAuth();
          break;
        case "signOut":
          setAuthState((prevState) => ({
            ...prevState,
            userType: UserType.STUDENT,
            nextStep: "",
            isSignedIn: false,
            user: undefined,
          }));
          break;
        default:
          break;
      }
    };

    const hubUnsubscribe = Hub.listen("auth", authListener);

    return () => {
      hubUnsubscribe();
    };
  }, [setAuthState]);
};

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const [authState, setAuthState] = useState<AuthState>(() => ({
    isSignedIn: false,
    userType: undefined,
    role: undefined,
    domain: "",
    nextStep: localStorage.getItem("nextStep") || "",
    user: undefined,
  }));
  const [logoutTimer, setLogoutTimer] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      setAuthState((prev) => ({
        ...prev,
        temporaryData: undefined,
      }));
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("nextStep", authState.nextStep);
  }, [authState.nextStep]);

  const handleSignOut = useCallback(async (): Promise<Error | void> => {
    try {
      await signOut({ global: true });
      localStorage.removeItem("nextStep");
      setAuthState((prevState) => ({
        ...prevState,
        nextStep: "SIGN_IN",
        isSignedIn: false,
        user: undefined,
      }));
      logoutTimer && clearTimeout(logoutTimer);
    } catch (error) {
      return error instanceof Error ? error : new Error(`Error signing out.`);
    }
  }, [logoutTimer]);

  useAuthCheck(setAuthState);
  useLogoutTimer(authState.user, setLogoutTimer, handleSignOut);

  const handleSignUp = async ({
    username,
    password,
    domain,
    userType,
  }: {
    username: string;
    password: string;
    domain: string;
    userType: UserType;
  }): Promise<Error | void> => {
    try {
      if (userType === "STUDENT") {
        domain = "";
      } else {
        if (!domain) {
          throw new Error("Domain is required for non-student users");
        }
        const extractedDomain = getDomain(domain);
        if (!extractedDomain) {
          throw new Error("Invalid domain format");
        }
        domain = extractedDomain;
      }
      const { nextStep } = await signUp({
        username: username.toLowerCase(),
        password: password,
        options: {
          userAttributes: {
            "custom:domain": domain,
            "custom:userType": userType,
          },
          autoSignIn: false,
        },
      });

      setAuthState((prevState) => ({
        ...prevState,
        nextStep: nextStep?.signUpStep || "",
        temporaryData: {
          username,
          signUpPassword: password,
        },
      }));
    } catch (error) {
      return error instanceof Error ? error : new Error(`Error signing up.`);
    }
  };

  const handleSignUpConfirmation = async ({
    username,
    confirmationCode,
  }: {
    username: string;
    confirmationCode: string;
  }): Promise<Error | void> => {
    try {
      await confirmSignUp({
        username,
        confirmationCode,
      });
    } catch (error) {
      return error instanceof Error
        ? error
        : new Error(`Error confirming sign up.`);
    }
  };

  const resendConfirmationCode = async (
    username: string,
  ): Promise<Error | void> => {
    try {
      await resendSignUpCode({ username });
      console.log("code resent successfully");
    } catch (error) {
      return error instanceof Error
        ? error
        : new Error(`Error resending code.`);
    }
  };

  const handleSignIn = async ({
    username,
    password,
  }: {
    username: string;
    password: string;
  }): Promise<Error | void> => {
    try {
      const output = await signIn({ username, password });
      handleSignInNextSteps(output, username);
    } catch (error) {
      return error instanceof Error ? error : new Error(`Error signing in.`);
    }
  };

  const handleSignInNextSteps = (output: any, username?: string): void => {
    const { nextStep } = output;

    if (nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
      const branch =
        process.env.AWS_BRANCH || process.env.BRANCH_NAME || "local";
      const totpSetupDetails = nextStep.totpSetupDetails;
      const userEmail = username;

      let appName: string;
      if (branch === "main") {
        appName = "Edubay";
      } else {
        appName = `Edubay-${branch}`;
      }

      const setupUri = totpSetupDetails.getSetupUri(appName, userEmail).href;

      setAuthState((prev) => ({
        ...prev,
        temporaryData: {
          sharedSecret: totpSetupDetails.sharedSecret,
          setupUri,
        },
      }));
    }

    setAuthState((prevState) => ({
      ...prevState,
      nextStep: nextStep?.signInStep || "",
    }));
  };

  const handleSignInConfirmation = async (
    totpCode: string,
  ): Promise<Error | void> => {
    try {
      const output = await confirmSignIn({ challengeResponse: totpCode });
      const { nextStep } = output;

      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString() ?? "";
      const payload = decodeJWT(token)?.payload;
      const userType = payload?.[`custom:userType`] as UserType;
      const role = payload?.[`custom:role`] as UserRole;
      const domain = payload?.[`custom:domain`] as string;

      setAuthState((prev) => ({
        ...prev,
        user: user || undefined,
        isSignedIn: true,
        nextStep: nextStep.signInStep,
        userType,
        role,
        domain,
        temporaryData: undefined,
      }));
    } catch (error) {
      return error instanceof Error
        ? error
        : new Error(`Error confirming sign in.`);
    }
  };

  const handleForceNewPassword = async (
    newPassword: string,
  ): Promise<Error | void> => {
    try {
      const output = await confirmSignIn({
        challengeResponse: newPassword,
      });
      handleSignInNextSteps(output);
    } catch (error) {
      return error instanceof Error
        ? error
        : new Error(`Error changing password.`);
    }
  };

  const handleResetPassword = async (
    username: string,
  ): Promise<Error | void> => {
    try {
      const output = await resetPassword({ username });
      handleResetPasswordNextSteps(output);
    } catch (error) {
      return error instanceof Error
        ? error
        : new Error(`Error resetting password.`);
    }
  };

  const handleResetPasswordNextSteps = (output: any): void => {
    const { nextStep } = output;
    setAuthState((prevState) => ({
      ...prevState,
      nextStep: nextStep.resetPasswordStep,
    }));
  };

  const handleConfirmResetPassword = async ({
    username,
    confirmationCode,
    newPassword,
  }: {
    username: string;
    confirmationCode: string;
    newPassword: string;
  }): Promise<Error | void> => {
    try {
      await confirmResetPassword({ username, confirmationCode, newPassword });
    } catch (error) {
      return error instanceof Error
        ? error
        : new Error(`Error confirming reset password.`);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        authState,
        setAuthState,
        handleSignUp,
        handleSignUpConfirmation,
        resendConfirmationCode,
        handleSignIn,
        handleSignInConfirmation,
        handleForceNewPassword,
        handleResetPassword,
        handleConfirmResetPassword,
        handleSignOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
